import { Badge } from "@/components/ui/badge";
import { ExternalBadge } from "@/components/molecules/external-badge";
import type { Service } from "@/features/services/types";
import { useTranslation } from "@/features/utils/i18n";
import type { ColumnDef } from "@tanstack/react-table";
import { Action } from "./columns-action";
import { formatCurrency } from "@/features/utils/numbers";

export const useServicesColumns = () => {
  const { t, i18n } = useTranslation("services");

  const columns: ColumnDef<Service>[] = [
    {
      accessorKey: "name",
      header: t("table.serviceName"),
      cell: ({ row }) => {
        const service = row.original;
        const isExternal = !!service.adapter;

        return (
          <div>
            <div className="font-medium flex items-center gap-2">
              {service.name}
              {isExternal && <ExternalBadge adapter={service.adapter!} />}
            </div>
            {service.description && (
              <div className="text-sm text-muted-foreground line-clamp-1">
                {service.description}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "duration",
      header: t("table.duration"),
      cell: ({ row }) => {
        const minutes = row.original.duration;
        if (!minutes) return <span className="text-muted-foreground">-</span>;

        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        if (hours > 0 && mins > 0) {
          return `${hours}h ${mins}m`;
        } else if (hours > 0) {
          return `${hours}h`;
        } else {
          return `${mins}m`;
        }
      },
    },
    {
      accessorKey: "price",
      header: t("table.price"),
      cell: ({ row }) => {
        const price = row.original.price;
        if (!price) return <span className="text-muted-foreground">-</span>;
        return formatCurrency(price, i18n.language, "EUR");
      },
    },
    {
      accessorKey: "performers",
      header: t("table.performers"),
      cell: ({ row }) => {
        const performerIds = row.original.performers?.ids || [];
        if (performerIds.length === 0) {
          return (
            <span className="text-muted-foreground">
              {t("performers.anyone")}
            </span>
          );
        }
        return (
          <Badge variant="secondary">
            {t("performers.members", { count: performerIds.length })}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("table.actions")}</div>,
      cell: ({ row }) => <Action service={row.original} />,
    },
  ];

  return columns;
};
